import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import {
	Heart,
	MapPin,
	Calendar,
	MessageSquare,
	Award,
	Clock,
	Package,
	Users,
	ArrowLeft,
	Briefcase,
	Play,
	Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";
import { BookingFlow } from "@/components/parent/BookingFlow";
// Removed PaymentFlow import - now using Stripe Checkout

interface Package {
	id: string;
	name: string;
	description: string;
	price: number;
	discount_percentage: number;
	is_active: boolean;
}

interface Certification {
	id: string;
	doula_listing_id?: string;
	name: string;
	description?: string;
	issue_date?: string;
	certificate_url?: string;
	created_at?: string;
}

interface Experience {
	id: string;
	title: string;
	description: string;
	start_date: string;
	end_date: string;
}

interface Doula {
	id: string;
	user_id: string; // Add user_id
	status?: string; // Add status field
	name: string;
	title: string;
	location:
	| string
	| { address?: string; city?: string; state?: string; zip?: string };
	distance: string;
	profile_image: string;
	intro_video_url: string | null;
	bio: string;
	experience_years: number;
	specialties: string[];
	certifications: Certification[];
	experience: Experience[];
	packages: Package[];
	is_favorite: boolean;
	is_verified: boolean;
	births_attended_range: string;
	// New profile preference fields
	additional_specialties?: string[];
	gender?: string;
	sexuality?: string;
	race?: string;
	religion?: string;
	style?: string[];
	doula_service_type?: string[];
	languages_spoken?: string[];
}

const DoulaProfile: React.FC = () => {
	const { doulaId } = useParams<{ doulaId: string }>();
	const navigate = useNavigate();
	const { user } = useAuth();
	const [doula, setDoula] = useState<Doula | null>(null);
	const [loading, setLoading] = useState(true);
	const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
	const [hasAppointment, setHasAppointment] = useState(false);
	const [existingConversation, setExistingConversation] = useState<
		string | null
	>(null);
	const [showBookingFlow, setShowBookingFlow] = useState(false);
	const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
	const [doulaUserId, setDoulaUserId] = useState<string | null>(null);
	const [appointmentHistory, setAppointmentHistory] = useState<any>(null);
	const [actionType, setActionType] = useState<
		"book_intro_call" | "purchase_package"
	>("book_intro_call");

	useEffect(() => {
		if (doulaId) {
			loadDoulaProfile();
			if (user) {
				checkAppointmentHistory();
			}
		}
	}, [doulaId, user]);

	// Handle Stripe checkout return
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const paymentStatus = urlParams.get("payment");
		const sessionId = urlParams.get("session_id");

		if (paymentStatus === "success" && sessionId) {
			// Clear URL parameters
			window.history.replaceState({}, document.title, window.location.pathname);

			// Show success message
			alert("Payment successful! Your package purchase has been completed.");

			// Refresh appointment history to reflect new relationship
			if (user) {
				checkAppointmentHistory();
			}

			// Clear selected package
			setSelectedPackage(null);
		} else if (paymentStatus === "cancelled") {
			// Clear URL parameters
			window.history.replaceState({}, document.title, window.location.pathname);

			// Show cancelled message
			alert("Payment was cancelled. You can try again anytime.");
		}
	}, [user]);

	// Get doula's user_id as fallback
	const getDoulaUserId = async () => {
		if (!doulaId) return;

		try {
			const { data, error } = await supabase
				.from("doula_listings")
				.select("user_id")
				.eq("id", doulaId)
				.single();

			if (!error && data) {
				setDoulaUserId(data.user_id);
			}
		} catch (error) {
			// Error getting doula user_id
		}
	};

	useEffect(() => {
		if (doulaId && !doula?.user_id) {
			getDoulaUserId();
		}
	}, [doulaId, doula?.user_id]);

	const loadDoulaProfile = async () => {
		if (!doulaId) return;

		try {
			// Fetch doula profile from edge function
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-doula-profile/${doulaId}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
					},
				},
			);

			const result = await response.json();

			if (!response.ok) {
				setDoula(null);
				return;
			}

			setDoula(result.data);
		} catch (error) {
			setDoula(null);
		} finally {
			setLoading(false);
		}
	};

	const toggleFavorite = async () => {
		if (!doula) return;

		if (!user) {
			// If not logged in, redirect to parent signup
			sessionStorage.setItem("selectedDoulaId", doula.id);
			sessionStorage.setItem(
				"parentSearchData",
				JSON.stringify({
					birthLocation: "Location from profile",
					dueDate: "Due date from profile",
				}),
			);

			navigate("/parent-signup", {
				state: {
					searchData: {
						birthLocation: "Location from profile",
						dueDate: "Due date from profile",
					},
					redirectTo: `/doula-profile/${doula.id}`,
					action: "favorite",
				},
			});
			return;
		}

		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/toggle-favorite`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						doula_listing_id: doula.id,
					}),
				},
			);

			const result = await response.json();

			if (response.ok) {
				setDoula((prev) =>
					prev ? { ...prev, is_favorite: result.action === "added" } : null,
				);
			}
		} catch (error) {
			// Error toggling favorite
		}
	};

	const handleBooking = () => {
		if (!user) {
			// If not logged in, redirect to parent signup
			sessionStorage.setItem("selectedDoulaId", doula?.id || "");
			sessionStorage.setItem(
				"parentSearchData",
				JSON.stringify({
					birthLocation: "Location from profile",
					dueDate: "Due date from profile",
				}),
			);

			navigate("/parent-signup", {
				state: {
					searchData: {
						birthLocation: "Location from profile",
						dueDate: "Due date from profile",
					},
					redirectTo: `/doula-profile/${doula?.id}`,
					action: "book",
				},
			});
			return;
		}

		// Open booking flow modal
		setShowBookingFlow(true);
	};

	const handleBookIntroCall = () => {
		if (!user) {
			// If not logged in, redirect to parent signup
			sessionStorage.setItem("selectedDoulaId", doula?.id || "");
			sessionStorage.setItem(
				"parentSearchData",
				JSON.stringify({
					birthLocation: "Location from profile",
					dueDate: "Due date from profile",
				}),
			);

			navigate("/parent-signup", {
				state: {
					searchData: {
						birthLocation: "Location from profile",
						dueDate: "Due date from profile",
					},
					redirectTo: `/doula-profile/${doula?.id}`,
					action: "book",
				},
			});
			return;
		}

		// Open booking flow modal
		setShowBookingFlow(true);
	};

	const handlePurchasePackage = async () => {
		if (!user) {
			// If not logged in, redirect to parent signup
			sessionStorage.setItem("selectedDoulaId", doula?.id || "");
			sessionStorage.setItem("selectedPackageId", selectedPackage || "");
			sessionStorage.setItem(
				"parentSearchData",
				JSON.stringify({
					birthLocation: "Location from profile",
					dueDate: "Due date from profile",
				}),
			);

			navigate("/parent-signup", {
				state: {
					searchData: {
						birthLocation: "Location from profile",
						dueDate: "Due date from profile",
					},
					redirectTo: `/doula-profile/${doula?.id}`,
					action: "purchase",
				},
			});
			return;
		}

		if (!selectedPackage || !doula) {
			alert("Please select a package first");
			return;
		}

		setIsCreatingCheckout(true);

		try {
			// Get the selected package data
			const packageData = doula.packages.find((p) => p.id === selectedPackage);
			if (!packageData) {
				throw new Error("Selected package not found");
			}

			// Get parent user ID
			const parentUserId = appointmentHistory?.parentUserId;
			if (!parentUserId) {
				throw new Error("Parent user information not available");
			}

			// Create checkout session
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						packageData: {
							id: packageData.id,
							name: packageData.name,
							description: packageData.description,
							price: packageData.price,
							discount_percentage: packageData.discount_percentage,
						},
						doulaUserId: doula.user_id || doulaUserId || doulaId!,
						parentUserId: parentUserId,
						successUrl: `${window.location.origin}/doula-profile/${doula.id}?payment=success`,
						cancelUrl: `${window.location.origin}/doula-profile/${doula.id}?payment=cancelled`,
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to create checkout session");
			}

			// Redirect to Stripe Checkout
			window.location.href = result.data.checkoutUrl;
		} catch (error) {
			console.error("Error creating checkout session:", error);
			alert(`Failed to start checkout: ${error.message}`);
		} finally {
			setIsCreatingCheckout(false);
		}
	};

	const checkAppointmentHistory = async () => {
		if (!user || !doulaId) return;

		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-appointment-history`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						doulaId: doulaId,
						parentAuthUserId: user.id,
					}),
				},
			);

			const result = await response.json();

			if (response.ok && result.data) {
				setAppointmentHistory(result.data);
				setHasAppointment(result.data.hasAppointments);
				setExistingConversation(result.data.existingConversation);
				setActionType(result.data.recommendedAction);

				// Update doula user ID if we got it from the API
				if (result.data.doulaUserId) {
					setDoulaUserId(result.data.doulaUserId);
				}
			}
		} catch (error) {
			console.error("Error checking appointment history:", error);
		}
	};

	const handleMessageDoula = () => {
		if (existingConversation) {
			// Navigate to existing conversation
			navigate("/parent-messages", {
				state: { conversationId: existingConversation },
			});
		} else {
			// Create new conversation and navigate
			createConversationAndNavigate();
		}
	};

	const createConversationAndNavigate = async () => {
		if (!user || !doulaId) return;

		try {
			// Get user profile
			const { data: userProfile } = await supabase
				.from("users")
				.select("id")
				.eq("auth_user_id", user.id)
				.single();

			if (!userProfile) return;

			// Get the first appointment with this doula
			const { data: appointment } = await supabase
				.from("appointments")
				.select("id")
				.eq("parent_user_id", userProfile.id)
				.eq("doula_user_id", doulaId)
				.in("status", ["pending", "accepted", "completed"])
				.single();

			if (!appointment) return;

			// Create conversation
			const { data: conversation, error } = await supabase
				.from("conversations")
				.insert([
					{
						parent_user_id: userProfile.id,
						doula_user_id: doulaId,
						appointment_id: appointment.id,
					},
				])
				.select()
				.single();

			if (error) {
				return;
			}

			// Navigate to messages with the new conversation
			navigate("/parent-messages", {
				state: { conversationId: conversation.id },
			});
		} catch (error) {
			// Error creating conversation
		}
	};

	const handleBackToSearch = () => {
		navigate("/search-doulas");
	};

	// Helper function to format date
	const formatDate = (dateString: string | null) => {
		if (!dateString) return "Present";
		try {
			return new Date(dateString).toLocaleDateString("en-US", {
				year: "numeric",
				month: "short",
			});
		} catch {
			return "Present";
		}
	};

	// Helper function to format certification date
	const formatCertificationDate = (dateString?: string | null) => {
		if (!dateString) return "Date not specified";
		try {
			return new Date(dateString).toLocaleDateString("en-US", {
				year: "numeric",
				month: "long",
				day: "numeric",
			});
		} catch {
			return "Date not specified";
		}
	};

	// Video embed helper function
	const getVideoEmbedUrl = (url: string): string | null => {
		if (!url) return null;

		// Clean URL and remove trailing slashes
		const cleanUrl = url.trim().replace(/\/$/, "");

		// YouTube
		if (cleanUrl.includes("youtube.com") || cleanUrl.includes("youtu.be")) {
			let videoId = "";
			if (cleanUrl.includes("youtu.be/")) {
				videoId = cleanUrl.split("youtu.be/")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("youtube.com/watch?v=")) {
				videoId = cleanUrl.split("v=")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("youtube.com/embed/")) {
				videoId = cleanUrl.split("embed/")[1].split(/[?&]/)[0];
			}
			return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
		}

		// Vimeo
		if (cleanUrl.includes("vimeo.com")) {
			let videoId = "";
			if (cleanUrl.includes("player.vimeo.com/video/")) {
				videoId = cleanUrl.split("player.vimeo.com/video/")[1].split(/[?&]/)[0];
			} else {
				videoId = cleanUrl.split("vimeo.com/")[1]?.split(/[?&]/)[0];
			}
			return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
		}

		// VEED.io
		if (cleanUrl.includes("veed.io")) {
			let videoId = "";
			if (cleanUrl.includes("veed.io/view/")) {
				videoId = cleanUrl.split("veed.io/view/")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("veed.io/embed/")) {
				videoId = cleanUrl.split("veed.io/embed/")[1].split(/[?&]/)[0];
			}
			return videoId ? `https://www.veed.io/embed/${videoId}` : null;
		}

		// Loom
		if (cleanUrl.includes("loom.com")) {
			let videoId = "";
			if (cleanUrl.includes("loom.com/share/")) {
				videoId = cleanUrl.split("loom.com/share/")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("loom.com/embed/")) {
				videoId = cleanUrl.split("loom.com/embed/")[1].split(/[?&]/)[0];
			}
			return videoId ? `https://www.loom.com/embed/${videoId}` : null;
		}

		// Google Drive
		if (cleanUrl.includes("drive.google.com")) {
			let fileId = "";
			if (cleanUrl.includes("/file/d/")) {
				fileId = cleanUrl.split("/file/d/")[1].split("/")[0];
			} else if (cleanUrl.includes("id=")) {
				fileId = cleanUrl.split("id=")[1].split(/[?&]/)[0];
			}
			return fileId
				? `https://drive.google.com/file/d/${fileId}/preview`
				: null;
		}

		// Wistia
		if (cleanUrl.includes("wistia.com") || cleanUrl.includes("wi.st")) {
			let videoId = "";
			if (cleanUrl.includes("wistia.com/medias/")) {
				videoId = cleanUrl.split("wistia.com/medias/")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("wi.st/medias/")) {
				videoId = cleanUrl.split("wi.st/medias/")[1].split(/[?&]/)[0];
			}
			return videoId ? `https://fast.wistia.net/embed/iframe/${videoId}` : null;
		}

		// Twitch
		if (cleanUrl.includes("twitch.tv")) {
			let videoId = "";
			if (cleanUrl.includes("twitch.tv/videos/")) {
				videoId = cleanUrl.split("twitch.tv/videos/")[1].split(/[?&]/)[0];
				return `https://player.twitch.tv/?video=${videoId}&parent=${window.location.hostname}`;
			} else if (cleanUrl.includes("clips.twitch.tv/")) {
				videoId = cleanUrl.split("clips.twitch.tv/")[1].split(/[?&]/)[0];
				return `https://clips.twitch.tv/embed?clip=${videoId}&parent=${window.location.hostname}`;
			}
		}

		// Dailymotion
		if (cleanUrl.includes("dailymotion.com") || cleanUrl.includes("dai.ly")) {
			let videoId = "";
			if (cleanUrl.includes("dailymotion.com/video/")) {
				videoId = cleanUrl.split("dailymotion.com/video/")[1].split(/[?&]/)[0];
			} else if (cleanUrl.includes("dai.ly/")) {
				videoId = cleanUrl.split("dai.ly/")[1].split(/[?&]/)[0];
			}
			return videoId
				? `https://www.dailymotion.com/embed/video/${videoId}`
				: null;
		}

		// Streamable
		if (cleanUrl.includes("streamable.com")) {
			const videoId = cleanUrl.split("streamable.com/")[1]?.split(/[?&]/)[0];
			return videoId ? `https://streamable.com/e/${videoId}` : null;
		}

		// Facebook Video
		if (cleanUrl.includes("facebook.com") && cleanUrl.includes("/videos/")) {
			const encodedUrl = encodeURIComponent(cleanUrl);
			return `https://www.facebook.com/plugins/video.php?href=${encodedUrl}`;
		}

		// Instagram Video (limited embed support)
		if (
			cleanUrl.includes("instagram.com/p/") ||
			cleanUrl.includes("instagram.com/reel/")
		) {
			const postId = cleanUrl.split(/\/p\/|\/reel\//)[1]?.split(/[?&\/]/)[0];
			return postId ? `https://www.instagram.com/p/${postId}/embed/` : null;
		}

		// If it's already an embed URL, return as is
		if (cleanUrl.includes("embed") || cleanUrl.includes("player")) {
			return cleanUrl;
		}

		// For other formats, return null
		return null;
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
					<div className="flex justify-center items-center h-64">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
					</div>
				</div>
			</div>
		);
	}

	if (!doula) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
					<div className="text-center py-8">
						<h2 className="text-2xl font-bold text-gray-900 mb-4">
							Doula Not Found
						</h2>
						<p className="text-gray-600">
							The doula profile you're looking for doesn't exist or is no longer
							available.
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Check if doula listing is not active
	if (doula.status && doula.status !== "active") {
		return (
			<div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
					{/* Back Navigation */}
					<div className="mb-6">
						<Button
							onClick={handleBackToSearch}
							className="flex items-center bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow font-karla"
						>
							<ArrowLeft className="h-4 w-4 mr-2" />
							Back to Search Results
						</Button>
					</div>

					<div className="max-w-2xl mx-auto">
						<Card className="p-8 shadow-xl text-center">
							<div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
								<Users className="h-12 w-12 text-gray-400" />
							</div>

							<h2 className="text-3xl font-light font-abc-prophet text-black mb-4">
								Doula Profile Not Available
							</h2>

							<div className="space-y-4 text-gray-600">
								<p className="text-lg">
									This doula's profile is currently not available for booking.
								</p>

								{doula.status === "pending" && (
									<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
										<div className="flex items-center justify-center mb-2">
											<Clock className="h-5 w-5 text-yellow-600 mr-2" />
											<span className="font-medium text-yellow-800">
												Application Under Review
											</span>
										</div>
										<p className="text-yellow-700 text-sm">
											This doula's application is currently being reviewed by
											our team.
										</p>
									</div>
								)}

								{doula.status === "inactive" && (
									<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
										<div className="flex items-center justify-center mb-2">
											<Users className="h-5 w-5 text-gray-600 mr-2" />
											<span className="font-medium text-gray-800">
												Profile Inactive
											</span>
										</div>
										<p className="text-gray-700 text-sm">
											This doula has temporarily paused their services.
										</p>
									</div>
								)}

								{doula.status === "suspended" && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-4">
										<div className="flex items-center justify-center mb-2">
											<Users className="h-5 w-5 text-red-600 mr-2" />
											<span className="font-medium text-red-800">
												Profile Suspended
											</span>
										</div>
										<p className="text-red-700 text-sm">
											This doula's profile is currently suspended.
										</p>
									</div>
								)}

								{doula.status &&
									!["pending", "inactive", "suspended"].includes(
										doula.status,
									) && (
										<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
											<div className="flex items-center justify-center mb-2">
												<Users className="h-5 w-5 text-gray-600 mr-2" />
												<span className="font-medium text-gray-800">
													Profile Unavailable
												</span>
											</div>
											<p className="text-gray-700 text-sm">
												This doula's profile is currently unavailable (Status:{" "}
												{doula.status}).
											</p>
										</div>
									)}
							</div>

							<div className="mt-8">
								<Button
									onClick={() => navigate("/search-doulas")}
									className="bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow font-karla"
								>
									<ArrowLeft className="h-4 w-4 mr-2" />
									Find Other Doulas
								</Button>
							</div>
						</Card>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
				{/* Back Navigation */}
				<div className="mb-6">
					<Button
						onClick={handleBackToSearch}
						className="flex items-center bg-primary-500 hover:bg-primary-600 text-white shadow-lg"
					>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Search Results
					</Button>
				</div>

				<div className="space-y-8">
					{/* Header */}
					<Card className="p-8 shadow-xl">
						<div className="flex flex-col lg:flex-row gap-8">
							{/* Profile Image */}
							<div className="flex-shrink-0">
								<div className="w-40 h-40 rounded-xl overflow-hidden bg-gray-100 shadow-xl border border-gray-200">
									<img
										src={doula.profile_image}
										alt={doula.name}
										className="w-full h-full object-cover"
										onError={(e) => {
											e.currentTarget.src = "/placeholder.svg";
										}}
									/>
								</div>
							</div>

							{/* Profile Info */}
							<div className="flex-1">
								<div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
									<div>
										<div className="flex items-center space-x-3 mb-2">
											<h1 className="text-4xl font-bold text-gray-900 font-karla">
												{doula.name}
											</h1>
											{doula.is_verified && (
												<Badge
													variant="secondary"
													className="bg-green-100 text-green-800"
												>
													Verified
												</Badge>
											)}
											{doula.is_favorite && (
												<Badge
													variant="secondary"
													className="bg-primary-100 text-primary-800"
												>
													❤️ Favorited
												</Badge>
											)}
										</div>
										<p className="text-xl text-gray-600 font-karla mb-3">
											{doula.title}
										</p>
										<div className="flex items-center space-x-4 text-gray-600 mb-4">
											<div className="flex items-center">
												<MapPin className="h-5 w-5 mr-2" />
												<span>
													{typeof doula.location === "object"
														? `${doula.location.city || ""}, ${doula.location.state || ""}`
															.replace(/^,\s*/, "")
															.replace(/,\s*$/, "")
														: doula.location || "Location not specified"}
												</span>
											</div>
											<div className="flex items-center">
												<Users className="h-5 w-5 mr-2" />
												<span>{doula.experience_years} years experience</span>
											</div>
											<div className="flex items-center">
												<Award className="h-5 w-5 mr-2" />
												<span>
													{doula.births_attended_range} births attended
												</span>
											</div>
										</div>
									</div>

									<div className="flex gap-3 mt-4 lg:mt-0">
										<Button
											variant="outline"
											onClick={toggleFavorite}
											className={
												doula.is_favorite ? "text-red-500 border-red-200" : ""
											}
										>
											<Heart
												className="h-4 w-4 mr-2"
												fill={doula.is_favorite ? "currentColor" : "none"}
											/>
											{doula.is_favorite ? "Favorited" : "Add to Favorites"}
										</Button>
										{hasAppointment && (
											<Button
												onClick={handleMessageDoula}
												variant="outline"
												className="border-green-200 text-green-700 hover:bg-green-50"
											>
												<MessageSquare className="h-4 w-4 mr-2" />
												{existingConversation
													? "Continue Chat"
													: "Message Doula"}
											</Button>
										)}
										{actionType === "book_intro_call" ? (
											<Button
												onClick={handleBookIntroCall}
												className="bg-primary-500 hover:bg-primary-600 text-white shadow-lg"
											>
												<Calendar className="h-4 w-4 mr-2" />
												Book Intro Call
											</Button>
										) : (
											<Button
												onClick={handlePurchasePackage}
												disabled={!selectedPackage || isCreatingCheckout}
												className="bg-green-500 hover:bg-green-600 text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
											>
												{isCreatingCheckout ? (
													<>
														<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
														Starting Checkout...
													</>
												) : (
													<>
														<Package className="h-4 w-4 mr-2" />
														{selectedPackage
															? "Purchase Package"
															: "Select Package First"}
													</>
												)}
											</Button>
										)}
									</div>
								</div>

								<p className="text-gray-700 leading-relaxed text-lg">
									{doula.bio}
								</p>
							</div>
						</div>
					</Card>

					{/* Specialties & Profile Information - Redistributed for Better Balance */}
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-start">
						{/* Left Column */}
						<div className="space-y-8 h-full flex flex-col">
							{/* Specialties */}
							<Card className="p-6 shadow-xl">
								<h3 className="text-xl font-semibold mb-6 flex items-center">
									<Award className="h-6 w-6 mr-3 text-primary-600" />
									Specialties
								</h3>
								<div className="flex flex-wrap gap-3">
									{(doula.specialties || []).map((specialty, index) => (
										<Badge
											key={index}
											variant="secondary"
											className="bg-purple-100 text-purple-700 text-sm"
										>
											{specialty}
										</Badge>
									))}
								</div>
							</Card>

							{/* Additional Specialties */}
							{doula.additional_specialties &&
								doula.additional_specialties.length > 0 && (
									<Card className="p-6 shadow-xl">
										<h3 className="text-xl font-semibold mb-6 flex items-center">
											<Award className="h-6 w-6 mr-3 text-primary-600" />
											Additional Certifications
										</h3>
										<div className="flex flex-wrap gap-3">
											{(doula.additional_specialties || []).map(
												(specialty, index) => (
													<Badge
														key={index}
														variant="secondary"
														className="bg-green-100 text-green-700 text-sm"
													>
														{specialty && specialty.length > 40
															? specialty.substring(0, 40) + "..."
															: specialty}
													</Badge>
												),
											)}
										</div>
									</Card>
								)}

							{/* Service Types */}
							{doula.doula_service_type &&
								doula.doula_service_type.length > 0 && (
									<Card className="p-6 shadow-xl">
										<h3 className="text-xl font-semibold mb-6 flex items-center">
											<Briefcase className="h-6 w-6 mr-3 text-primary-600" />
											Service Types
										</h3>
										<div className="flex flex-wrap gap-3">
											{(doula.doula_service_type || []).map((type, index) => (
												<Badge
													key={index}
													variant="secondary"
													className="bg-blue-100 text-blue-700 text-sm"
												>
													{type}
												</Badge>
											))}
										</div>
									</Card>
								)}

							{/* Doula Style */}
							{doula.style && doula.style.length > 0 && (
								<Card className="p-6 shadow-xl">
									<h3 className="text-xl font-semibold mb-6 flex items-center">
										<Heart className="h-6 w-6 mr-3 text-primary-600" />
										Doula Style
									</h3>
									<div className="flex flex-wrap gap-3">
										{(doula.style || []).map((styleItem, index) => (
											<Badge
												key={index}
												variant="secondary"
												className="bg-pink-100 text-pink-700 text-sm"
											>
												{styleItem}
											</Badge>
										))}
									</div>
								</Card>
							)}

							{/* Languages Spoken */}
							{doula.languages_spoken && doula.languages_spoken.length > 0 && (
								<Card className="p-6 shadow-xl">
									<h3 className="text-xl font-semibold mb-6 flex items-center">
										<MessageSquare className="h-6 w-6 mr-3 text-primary-600" />
										Languages Spoken
									</h3>
									<div className="flex flex-wrap gap-3">
										{(doula.languages_spoken || []).map((language, index) => (
											<Badge
												key={index}
												variant="secondary"
												className="bg-indigo-100 text-indigo-700 text-sm"
											>
												{language}
											</Badge>
										))}
									</div>
								</Card>
							)}
						</div>

						{/* Right Column */}
						<div className="space-y-8 h-full flex flex-col">
							{/* Intro Video */}
							{doula.intro_video_url && (
								<Card className="p-6 shadow-xl">
									<h3 className="text-xl font-semibold mb-6 flex items-center">
										<Play className="h-6 w-6 mr-3 text-primary-600" />
										Introduction Video
									</h3>
									<div className="flex-1 flex flex-col justify-between">
										{(() => {
											const embedUrl = getVideoEmbedUrl(doula.intro_video_url);

											if (embedUrl) {
												return (
													<div className="relative w-full flex-1 flex flex-col">
														{/* Video Frame - inspired by the hero section example */}
														<div className="-m-2 rounded-xl bg-gray-900/5 p-2 ring-1 ring-gray-900/10 ring-inset lg:-m-4 lg:rounded-2xl lg:p-4">
															{/* Video Container with Aspect Ratio */}
															<div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-md bg-gray-50 shadow-xl ring-1 ring-gray-900/10">
																<iframe
																	src={embedUrl}
																	title="Doula Intro Video"
																	className="absolute top-0 left-0 w-full h-full border-0 rounded-md"
																	allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
																	allowFullScreen
																/>
															</div>
														</div>
													</div>
												);
											} else {
												return (
													<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 flex-1 flex flex-col justify-between">
														<div>
															<div className="flex items-center">
																<Play className="w-8 h-8 text-yellow-600 mr-3" />
																<div>
																	<h4 className="font-semibold text-yellow-800 font-karla">
																		Video Format Not Supported
																	</h4>
																	<p className="text-yellow-700 font-karla text-sm mt-1">
																		This video format cannot be embedded. Click
																		below to view in a new tab.
																	</p>
																</div>
															</div>
														</div>
														<div className="mt-8">
															<a
																href={doula.intro_video_url}
																target="_blank"
																rel="noopener noreferrer"
																className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
															>
																<Eye className="w-4 h-4 mr-2" />
																Open Video in New Tab
															</a>
														</div>
													</div>
												);
											}
										})()}
									</div>
								</Card>
							)}

							{/* Background Information */}
							{(doula.gender || doula.race || doula.religion) && (
								<Card className="p-6 shadow-xl">
									<h3 className="text-xl font-semibold mb-6 flex items-center">
										<Users className="h-6 w-6 mr-3 text-primary-600" />
										Background
									</h3>
									<div className="space-y-3">
										{doula.gender && (
											<div className="flex items-center">
												<span className="font-medium text-gray-700 w-20">
													Gender:
												</span>
												<span className="text-gray-600">{doula.gender}</span>
											</div>
										)}
										{doula.race && (
											<div className="flex items-center">
												<span className="font-medium text-gray-700 w-20">
													Race:
												</span>
												<span className="text-gray-600">{doula.race}</span>
											</div>
										)}
										{doula.religion && (
											<div className="flex items-center">
												<span className="font-medium text-gray-700 w-20">
													Religion:
												</span>
												<span className="text-gray-600">{doula.religion}</span>
											</div>
										)}
									</div>
								</Card>
							)}

							{/* Certifications */}
							<Card className="p-6 shadow-xl flex-1">
								<h3 className="text-xl font-semibold mb-6 flex items-center">
									<Award className="h-6 w-6 mr-3 text-primary-600" />
									Certifications
								</h3>
								<div className="space-y-3">
									{(doula.certifications || []).length > 0 ? (
										(doula.certifications || []).map((cert, index) => (
											<div
												key={cert.id || index}
												className="border-l-4 border-primary-600 pl-4"
											>
												<div className="font-medium text-gray-900">
													{cert.name || "Unnamed Certification"}
												</div>
												{cert.description && (
													<div className="text-sm text-gray-600">
														{cert.description}
													</div>
												)}
												<div className="text-xs text-gray-500 mt-1">
													Issued: {formatCertificationDate(cert.issue_date)}
												</div>
											</div>
										))
									) : (
										<p className="text-gray-500 italic">
											No certifications listed
										</p>
									)}
								</div>
							</Card>
						</div>
					</div>

					{/* Packages */}
					<Card className="p-8 shadow-xl">
						<div className="flex items-center justify-between mb-8">
							<h3 className="text-2xl font-semibold flex items-center">
								<Package className="h-6 w-6 mr-3 text-primary-600" />
								Service Packages
							</h3>
							{selectedPackage && actionType === "purchase_package" && (
								<Button
									onClick={handlePurchasePackage}
									disabled={isCreatingCheckout}
									className="bg-green-500 hover:bg-green-600 text-white shadow-lg disabled:opacity-50"
								>
									{isCreatingCheckout ? (
										<>
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
											Starting Checkout...
										</>
									) : (
										<>
											<Package className="h-5 w-5 mr-2" />
											Purchase Selected Package
										</>
									)}
								</Button>
							)}
						</div>
						{(doula.packages || []).length > 0 ? (
							<>
								<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
									{(doula.packages || []).map((pkg) => (
										<motion.div
											key={pkg.id}
											className={`border rounded-xl p-6 cursor-pointer transition-all duration-200 shadow-lg ${selectedPackage === pkg.id
												? "border-primary-600 bg-primary-50 shadow-xl"
												: "border-gray-200 hover:border-gray-300 hover:shadow-xl"
												}`}
											onClick={() => setSelectedPackage(pkg.id)}
											whileHover={{ scale: 1.02 }}
											whileTap={{ scale: 0.98 }}
										>
											<h4 className="font-semibold text-xl mb-3">{pkg.name}</h4>
											<p className="text-gray-600 mb-4">{pkg.description}</p>
											<div className="text-3xl font-bold text-primary-600 mb-4">
												{pkg.discount_percentage > 0 ? (
													<>
														$
														{(
															pkg.price -
															(pkg.price * pkg.discount_percentage) / 100
														).toFixed(0)}
														<span className="text-sm text-gray-500 ml-2 line-through">
															${pkg.price.toFixed(0)}
														</span>
													</>
												) : (
													`$${pkg.price}`
												)}
											</div>
											{pkg.discount_percentage > 0 && (
												<Badge
													variant="secondary"
													className="bg-green-100 text-green-800 mb-4"
												>
													{pkg.discount_percentage}% off
												</Badge>
											)}
										</motion.div>
									))}
								</div>
							</>
						) : (
							<div className="text-center py-8">
								<Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
								<p className="text-gray-600">
									No packages available at this time.
								</p>
							</div>
						)}
					</Card>
				</div>
			</div>

			{/* Booking Flow Modal */}
			{showBookingFlow && doula && (
				<div>
					<BookingFlow
						doula={{
							id: doula.id,
							user_id: doula.user_id || doulaUserId || doulaId!, // Fallback to doulaId if user_id not available
							name: doula.name,
							title: doula.title,
							location: doula.location,
							profile_image: doula.profile_image,
							bio: doula.bio,
							experience_years: doula.experience_years,
							specialties: doula.specialties,
							google_calendar_id: "", // This will be fetched by the edge function
							availability_guardrails: "",
							booking_window_months: 3,
							is_verified: doula.is_verified,
							created_at: new Date().toISOString(),
							updated_at: new Date().toISOString(),
						}}
						onComplete={() => {
							setShowBookingFlow(false);
							// Refresh appointment status
							checkAppointmentHistory();
						}}
						onCancel={() => setShowBookingFlow(false)}
					/>
				</div>
			)}

			{/* Checkout sessions are handled by redirecting to Stripe */}
		</div>
	);
};

export default DoulaProfile;
